import { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Modal, Table, Button, Form, DatePicker, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import {
  getCommercialDateEditions,
  addCommercialDateEdition,
  editCommercialDateEdition,
  deleteCommercialDateEdition,
} from "../../../../api/Services";
import styles from "../viewcommercialdate/ViewCommercialDate.module.scss";

interface CommercialDateEditionsProps {
  commercialDateId: number;
}

const CommercialDateEditions = ({
  commercialDateId,
}: CommercialDateEditionsProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [editions, setEditions] = useState<any[]>([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingEdition, setEditingEdition] = useState<any>(null);
  const [selectedStartingDate, setSelectedStartingDate] = useState<any>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    handleFetchEditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercialDateId]);

  const handleFetchEditions = async () => {
    setLoading(true);
    const response = await getCommercialDateEditions(commercialDateId);
    setEditions(response);
    setLoading(false);
  };

  const handleOpenAddForm = () => {
    setEditingEdition(null);
    setSelectedStartingDate(null);
    form.resetFields();
    setIsFormVisible(true);
  };

  const handleOpenEditForm = (record: any) => {
    setEditingEdition(record);
    setSelectedStartingDate(moment(record.startingDate, "YYYY-MM-DD"));
    form.setFieldsValue({
      startingDate: moment(record.startingDate, "YYYY-MM-DD"),
      endingDate: moment(record.endingDate, "YYYY-MM-DD"),
    });
    setIsFormVisible(true);
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setEditingEdition(null);
    setSelectedStartingDate(null);
    form.resetFields();
  };

  const handleSubmitForm = async () => {
    await form.validateFields().then(async (values) => {
      const startingDate = moment(values.startingDate, "YYYY-MM-DD").format(
        "YYYY-MM-DD"
      );
      const endingDate = moment(values.endingDate, "YYYY-MM-DD").format(
        "YYYY-MM-DD"
      );

      const result = editingEdition
        ? await editCommercialDateEdition(
            commercialDateId,
            editingEdition.id,
            startingDate,
            endingDate
          )
        : await addCommercialDateEdition(
            commercialDateId,
            moment(values.startingDate).year(),
            startingDate,
            endingDate
          );

      if (result.success) {
        message.success(
          editingEdition
            ? t("Common-EditSuccessMessage")
            : t("Common-CreateSuccessMessage")
        );
        setIsFormVisible(false);
        setEditingEdition(null);
        form.resetFields();
        handleFetchEditions();
      } else {
        message.error(result.message || t("Common-ErrorMessage"));
      }
    });
  };

  const handleDeleteEdition = (record: any) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk: async () => {
        const success = await deleteCommercialDateEdition(
          commercialDateId,
          record.id
        );
        if (success) {
          message.success(t("Common-DeleteSuccessMessage"));
          handleFetchEditions();
        } else message.error(t("Common-ErrorMessage"));
      },
    });
  };

  const columns = [
    {
      title: t("Common-Year"),
      dataIndex: "year",
    },
    {
      title: t("Common-StartDate"),
      dataIndex: "startingDate",
      render: (date: string) => moment(date, "YYYY-MM-DD").format("DD-MM-YYYY"),
    },
    {
      title: t("Common-EndDate"),
      dataIndex: "endingDate",
      render: (date: string) => moment(date, "YYYY-MM-DD").format("DD-MM-YYYY"),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditForm(record)}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteEdition(record)}
          />
        </>
      ),
    },
  ];

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.tablesHeader}>
        <h2 className={styles.tableTitle}>
          {t("Common-CommercialDateEditions")}
        </h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAddForm}>
          {t("Common-AddEdition")}
        </Button>
      </div>

      <Table
        className={styles.table}
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={editions}
        pagination={false}
        locale={{ emptyText: t("Common-NoEditionsYet") }}
      />

      <Modal
        title={
          editingEdition ? t("Common-EditEdition") : t("Common-AddEdition")
        }
        visible={isFormVisible}
        onOk={handleSubmitForm}
        onCancel={handleCancelForm}
        okText={t("Common-Save")}
        cancelText={t("Common-Cancel")}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("Common-StartDate")}
            name="startingDate"
            rules={[{ required: true, message: t("Common-StartDate") }]}
          >
            <DatePicker
              className={styles.dateSelector}
              format="YYYY-MM-DD"
              onChange={(date) => setSelectedStartingDate(date)}
            />
          </Form.Item>
          <Form.Item
            label={t("Common-EndDate")}
            name="endingDate"
            dependencies={["startingDate"]}
            rules={[
              { required: true, message: t("Common-EndDate") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startingDate = getFieldValue("startingDate");
                  if (!value || !startingDate || !value.isBefore(startingDate, "day")) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("Common-EndDateBeforeStartDate"))
                  );
                },
              }),
            ]}
          >
            <DatePicker
              className={styles.dateSelector}
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                !!selectedStartingDate &&
                !!current &&
                current.isBefore(selectedStartingDate, "day")
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CommercialDateEditions;
